import React, { useRef } from "react";
import { CardBody, CardFooter, Card, CardHeader } from "../card/card.jsx";
import ReactH5AudioPlayer from "react-h5-audio-player";
import 'react-h5-audio-player/lib/styles.css';
import { getAuthHeaders } from "../../misc.js";

const SongSelector = ({
  loading,
  isGeneratingSongs,
  handleGenerateAlignment,
  songs,
  handleSelectSong,
  setSongs,
  songReelId
}) => {
  const audioRef = useRef(null);

  // Helper: Write a string to a DataView at a given offset.
  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  // Helper: Convert an AudioBuffer to a WAV file Blob.
  const encodeWAV = (audioBuffer) => {
    const numChannels = audioBuffer.numberOfChannels;
    const sampleRate = audioBuffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;
    const numSamples = audioBuffer.length * numChannels;
    const buffer = new ArrayBuffer(44 + numSamples * 2);
    const view = new DataView(buffer);
    
    // RIFF header.
    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + numSamples * 2, true);
    writeString(view, 8, 'WAVE');
    // fmt subchunk.
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); // Subchunk1Size for PCM.
    view.setUint16(20, format, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, (sampleRate * numChannels * bitDepth) / 8, true);
    view.setUint16(32, (numChannels * bitDepth) / 8, true);
    view.setUint16(34, bitDepth, true);
    // data subchunk.
    writeString(view, 36, 'data');
    view.setUint32(40, numSamples * 2, true);

    // Write PCM samples.
    let offset = 44;
    for (let i = 0; i < audioBuffer.length; i++) {
      for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
        let sample = audioBuffer.getChannelData(channel)[i];
        sample = Math.max(-1, Math.min(1, sample));
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
        offset += 2;
      }
    }
    return new Blob([view], { type: "audio/wav" });
  };

  // Update the currently selected song's audio URL.
  const updateSongAudio = (newUrl) => {
    const updatedSongs = songs.map((song) => {
      if (song.selected) {
        return { ...song, audio_url: newUrl };
      }
      return song;
    });
    setSongs(updatedSongs);
  };

  // Generic trimming function.
  const trimAudio = async (removeType) => {
    const audioPlayer = audioRef.current;
    if (!audioPlayer || !audioPlayer.audio.current) return;
    
    const currentTime = audioPlayer.audio.current.currentTime;
    const selectedSong = songs.find((song) => song.selected);
    if (!selectedSong) return;

    try {
      // Fetch and decode the original audio.
      const response = await fetch("https://api.moatoverflow.com/music/" + selectedSong.audio_url);
      const arrayBuffer = await response.arrayBuffer();
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const originalBuffer = await audioCtx.decodeAudioData(arrayBuffer);
      const sampleRate = originalBuffer.sampleRate;
      let newBuffer;

      if (removeType === "before") {
        // Remove everything before currentTime.
        const startSample = Math.floor(currentTime * sampleRate);
        const newLength = originalBuffer.length - startSample;
        newBuffer = audioCtx.createBuffer(
          originalBuffer.numberOfChannels,
          newLength,
          sampleRate
        );
        for (let channel = 0; channel < originalBuffer.numberOfChannels; channel++) {
          const oldData = originalBuffer.getChannelData(channel);
          const newData = newBuffer.getChannelData(channel);
          for (let i = 0; i < newLength; i++) {
            newData[i] = oldData[i + startSample];
          }
        }
      } else if (removeType === "after") {
        // Remove everything after currentTime.
        const endSample = Math.floor(currentTime * sampleRate);
        newBuffer = audioCtx.createBuffer(
          originalBuffer.numberOfChannels,
          endSample,
          sampleRate
        );
        for (let channel = 0; channel < originalBuffer.numberOfChannels; channel++) {
          const oldData = originalBuffer.getChannelData(channel);
          const newData = newBuffer.getChannelData(channel);
          for (let i = 0; i < endSample; i++) {
            newData[i] = oldData[i];
          }
        }
      }

      // Convert the new AudioBuffer to a WAV file Blob.
      const wavBlob = encodeWAV(newBuffer);
      
      // Upload the trimmed audio file.
      const formData = new FormData();
      formData.append("audio", wavBlob, "trimmed.mp3");
      const uploadResponse = await fetch("https://api.moatoverflow.com/v1/song_reel/upload/audio", {
        method: "POST",
        body: formData,
        headers: {}
      });
      const uploadResult = await uploadResponse.json();
      if (uploadResult.success) {
        updateSongAudio(uploadResult.audio_url);

        // Calculate the removed time based on the type of cut.
        let removedTime = 0;
        if (removeType === "before") {
          removedTime = currentTime;
        } else if (removeType === "after") {
          removedTime = originalBuffer.duration - currentTime;
        }

        // Determine the selected song index.
        const selectedSongIndex = songs.findIndex(song => song.selected);

        // Send PUT request to update the song reel document with the cut info.
        await fetch(`https://api.moatoverflow.com/v1/song_reel/cuts/${songReelId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders()
          },
          body: JSON.stringify({
            songIndex: selectedSongIndex,
            cut: { type: removeType, time: removedTime }
          })
        });
      } else {
        console.error("Upload failed:", uploadResult.message);
      }
    } catch (error) {
      console.error("Error trimming audio:", error);
    }
  };

  const handleRemoveBefore = async () => {
    await trimAudio("before");
  };

  const handleRemoveAfter = async () => {
    await trimAudio("after");
  };

  return (
    <Card>
      <CardHeader>
        <h4>Select a Generated Song</h4>
      </CardHeader>
      <CardBody>
        {isGeneratingSongs && songs.length === 0 ? (
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Generating song files...</p>
          </div>
        ) : (
          <div className="d-flex flex-wrap">
            {songs.map((song, index) => (
              <button
                key={index}
                className={`btn me-2 mb-2 ${song.selected ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => handleSelectSong(index)}
              >
                Song {index + 1}
              </button>
            ))}
          </div>
        )}
      </CardBody>
      <CardFooter>
        {songs.map((song, index) => {
          if (song.selected) {
            return (
              <div key={index} className="mt-2">
                <ReactH5AudioPlayer
                  ref={audioRef}
                  src={
                    song.audio_url.startsWith("http")
                      ? song.audio_url
                      : "https://api.moatoverflow.com/music/" + song.audio_url
                  }
                  autoPlay={false}
                  controls
                  onPlay={() => console.log("Playing:", song.title)}
                />
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <div>
                    <button className="btn btn-danger me-2" onClick={handleRemoveBefore}>
                      Remove Before
                    </button>
                    <button className="btn btn-danger" onClick={handleRemoveAfter}>
                      Remove After
                    </button>
                  </div>
                  <div>
                    <button className="btn btn-warning" onClick={handleGenerateAlignment} disabled={loading}>
                      {loading ? "Generating Alignment..." : "Generate Alignment"}
                    </button>
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })}
      </CardFooter>
    </Card>
  );
};

export default SongSelector;
